import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rgpdService } from '@/services/rgpd'
import type {
  DataRequestFilters,
  DataRequestType,
  ExportFormat,
  DataProcessing,
} from '@/types/rgpd'

// STORY-046 to STORY-048: RGPD Hooks

// ============================================
// STORY-046: Liste des regles RGPD
// ============================================

export function useRGPDCategories() {
  return useQuery({
    queryKey: ['rgpd', 'categories'],
    queryFn: () => rgpdService.getRGPDCategories(),
  })
}

export function useDataProcessings() {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['rgpd', 'processings'],
    queryFn: () => rgpdService.getDataProcessings(),
  })

  const createMutation = useMutation({
    mutationFn: (data: Omit<DataProcessing, 'id' | 'created_at' | 'updated_at'>) =>
      rgpdService.createDataProcessing(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rgpd', 'processings'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<DataProcessing> }) =>
      rgpdService.updateDataProcessing(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rgpd', 'processings'] })
    },
  })

  return {
    processings: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    createProcessing: createMutation.mutateAsync,
    updateProcessing: updateMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  }
}

// ============================================
// STORY-047: Verification de conformite
// ============================================

export function useComplianceStats() {
  return useQuery({
    queryKey: ['rgpd', 'stats'],
    queryFn: () => rgpdService.getComplianceStats(),
    refetchInterval: 60000, // Rafraichir toutes les minutes
  })
}

export function useDataAlerts() {
  return useQuery({
    queryKey: ['rgpd', 'alerts'],
    queryFn: () => rgpdService.checkDataAlerts(),
  })
}

export function useRunComplianceAudit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => rgpdService.runComplianceAudit(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rgpd'] })
    },
  })
}

export function useExportComplianceReport() {
  return useMutation({
    mutationFn: () => rgpdService.exportComplianceReport(),
  })
}

// ============================================
// STORY-048: Export/Suppression des donnees
// ============================================

export function useDataRequests(filters?: DataRequestFilters) {
  return useQuery({
    queryKey: ['rgpd', 'requests', filters],
    queryFn: () => rgpdService.getDataRequests(filters),
  })
}

export function useDataRequest(id: string) {
  return useQuery({
    queryKey: ['rgpd', 'requests', id],
    queryFn: () => rgpdService.getDataRequest(id),
    enabled: !!id,
  })
}

export function useCreateDataRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: {
      client_id: string
      client_name: string
      client_email: string
      type: DataRequestType
      format?: ExportFormat
      reason?: string
    }) => rgpdService.createDataRequest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rgpd', 'requests'] })
      queryClient.invalidateQueries({ queryKey: ['rgpd', 'stats'] })
    },
  })
}

export function useProcessDataRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      action,
      adminId,
      notes,
    }: {
      id: string
      action: 'approve' | 'reject'
      adminId: string
      notes?: string
    }) => rgpdService.processDataRequest(id, action, adminId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rgpd', 'requests'] })
      queryClient.invalidateQueries({ queryKey: ['rgpd', 'stats'] })
    },
  })
}

export function useCompleteDataRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      adminId,
      result,
    }: {
      id: string
      adminId: string
      result: { export_url?: string; notes?: string }
    }) => rgpdService.completeDataRequest(id, adminId, result),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rgpd', 'requests'] })
      queryClient.invalidateQueries({ queryKey: ['rgpd', 'stats'] })
    },
  })
}

export function useGenerateClientExport() {
  return useMutation({
    mutationFn: (clientId: string) => rgpdService.generateClientDataExport(clientId),
  })
}

export function useDeleteClientData() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (clientId: string) => rgpdService.deleteClientData(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rgpd'] })
      queryClient.invalidateQueries({ queryKey: ['clients'] })
    },
  })
}

export function useExportToFormat() {
  return useMutation({
    mutationFn: ({ data, format }: { data: any; format: ExportFormat }) =>
      rgpdService.exportToFormat(data, format),
  })
}
